import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import { useGetBarLocationReportQuery } from '../../redux/api/barDeliveryApi';
import FormatDateTime from '../../components/dateTime/FormatDateTime';
import BarDeliveryTypeBadge from '../BarDelivery/BarDeliveryTypeBadge';

const BarLocationDetails = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useGetBarLocationReportQuery(
    locationId ?? skipToken
  );

  if (isLoading) return <p className='text-muted'>Lade Bar-Details...</p>;
  if (!data) {
    return (
      <div className='alert alert-danger'>Fehler beim Laden der Bar-Daten.</div>
    );
  }

  return (
    <>
      <h4>
        {data.bar_location_name}
        <span className='badge bg-dark ms-2'>Bar</span>
      </h4>
      <p className='text-muted'>Partner: {data.partner_name || '—'}</p>

      <h5 className='mt-4'>Aktuell bei der Bar</h5>
      {data.items.length === 0 ? (
        <p className='text-muted'>Noch keine bestätigten Lieferungen.</p>
      ) : (
        <table className='table table-striped align-middle'>
          <thead>
            <tr>
              <th>Artikel</th>
              <th className='text-end'>Bestand (netto geliefert)</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.article_id}>
                <td>{item.article_name}</td>
                <td className='text-end'>{item.delivered_quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pfand is tracked per Gebinde, not per article — a returned crate does
          not remember which article it held. */}
      {data.deposit_groups.length > 0 && (
        <>
          <h5 className='mt-5'>Offenes Leergut</h5>
          <table className='table table-striped align-middle'>
            <thead>
              <tr>
                <th>Pfandgruppe</th>
                <th className='text-end'>noch offen (Gebinde)</th>
              </tr>
            </thead>
            <tbody>
              {data.deposit_groups.map((group) => (
                <tr key={group.deposit_group_id}>
                  <td>
                    {group.deposit_group_name}
                    <div className='text-muted small'>
                      {group.return_kinds
                        .map((kind) => kind.name)
                        .join(' · ')}
                    </div>
                  </td>
                  <td
                    className={`text-end ${
                      Number(group.outstanding) < 0
                        ? 'text-warning-emphasis'
                        : ''
                    }`}
                  >
                    <b>
                      {Number(group.outstanding).toLocaleString('de-DE', {
                        maximumFractionDigits: 2,
                      })}
                    </b>
                    {Number(group.outstanding) < 0 && (
                      <span
                        className='badge bg-warning text-dark ms-2'
                        title='Mehr Leergut zurück als geliefert — bei gemischten Paletten normal.'
                      >
                        Überhang
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h5 className='mt-5'>Lieferungen</h5>
      {data.deliveries.length === 0 ? (
        <p className='text-muted'>Keine Lieferungen an diese Bar.</p>
      ) : (
        <table className='table table-striped align-middle'>
          <thead>
            <tr>
              <th>Nr.</th>
              <th>angelegt</th>
              <th>bestätigt</th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {data.deliveries.map((delivery) => (
              <tr key={delivery.id}>
                <td>
                  {delivery.counter}
                  <BarDeliveryTypeBadge
                    type={delivery.type}
                    cancelled={delivery.cancelled}
                  />
                </td>
                <td>
                  <FormatDateTime dateTime={delivery.created_at} />
                </td>
                <td>
                  {delivery.confirmed ? (
                    <FormatDateTime dateTime={delivery.confirmed_at} />
                  ) : (
                    <span className='badge bg-warning text-dark'>offen</span>
                  )}
                </td>
                <td>
                  <button
                    className='btn btn-primary btn-sm'
                    onClick={() => navigate(`/bar-delivery/${delivery.id}`)}
                  >
                    <FontAwesomeIcon
                      icon={faUpRightFromSquare}
                      className='me-2'
                    />
                    öffnen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

export default BarLocationDetails;
